import {
    getAuthToken,
    getNBAId,
    getAccessToken,
    getRefreshToken,
    retireHostOnlySessionTokens,
    setAccessToken,
    setRefreshToken
} from '../utils/cookieutils';
import { IActionContext } from '@msdyn365-commerce/core-internal';
import { lAC_GetRefreshTokenAsync } from '../custom-actions/DataActionExtension.g';
import { ILAC_AuthToken } from '../custom-actions/DataServiceEntities.g';
import { AccessErrorReason, AccessErrorStateInput } from '../actions/access-error-state.action';
import { dedupeInFlight } from './inflight';
import { accessErrorMessage } from './accesserrormessages';
import { isRefreshUnanswered } from './refreshfailure';

/** Not a refusal: the guest had access and the session ran out. */
const SESSION_EXPIRED_REASON: AccessErrorReason = 'session-expired';
const SESSION_EXPIRED_MESSAGE = 'Your session has timed out. Please open the menu again from the Clippers app.';

const REFRESH_IN_FLIGHT_KEY = 'authService.refreshToken';

/**
 * The identity provider rotates the refresh token: a successful refresh returns a new one and
 * invalidates the one presented. Concurrent callers each read the stored token before any of them
 * writes, so they all present the same value and every caller after the first is rejected with
 * INVALID_SESSION - and then reports an access error, ending a session that is in fact healthy.
 * Every caller therefore shares one refresh.
 */
export const refreshToken = (actionContext: IActionContext): Promise<ILAC_AuthToken | null> =>
    dedupeInFlight(REFRESH_IN_FLIGHT_KEY, async () => performRefresh(actionContext));

const isExpiredToken = (error: unknown): boolean => (error as { data?: { ErrorResourceId?: string } })?.data?.ErrorResourceId === '401';

/**
 * Runs an authorized call, and once more on a refreshed token when the first was refused for an expired one.
 */
export const withFreshToken = async <T>(
    actionContext: IActionContext,
    authToken: ILAC_AuthToken,
    call: (carried: ILAC_AuthToken) => Promise<T>
): Promise<T> => {
    try {
        return await call(authToken);
    } catch (error) {
        if (!isExpiredToken(error)) {
            throw error;
        }

        const refreshed = await refreshToken(actionContext);
        if (!refreshed) {
            throw error;
        }

        return call(refreshed);
    }
};

/**
 * The domain the app the guest arrives from scopes the session cookies to, as the site is authored. Without
 * it the shared cookie cannot be written, and a refreshed token reaches only this host - leaving the shared
 * copy holding the refresh token the identity provider has just revoked.
 */
const readSessionCookieDomain = (actionContext: IActionContext): string | undefined => {
    const authored = actionContext.requestContext?.app?.config?.sessionCookieDomain;
    if (typeof authored === 'string' && authored.trim()) {
        return authored.trim();
    }

    console.log('LTS Info', 'authService: no session cookie domain is authored, so the refreshed token reaches only this host');

    return undefined;
};

const performRefresh = async (actionContext: IActionContext): Promise<ILAC_AuthToken | null> => {
    const nbaId = getNBAId();
    const accessToken = getAccessToken();
    const refreshTokenValue = getRefreshToken();

    if (!nbaId || !accessToken || !refreshTokenValue) {
        actionContext.update(new AccessErrorStateInput(), {
            isAccessError: true,
            reason: SESSION_EXPIRED_REASON,
            message: SESSION_EXPIRED_MESSAGE
        });
        return null;
    }

    try {
        const refreshResult = await lAC_GetRefreshTokenAsync(
            { callerContext: actionContext },
            { RefreshToken: refreshTokenValue, Token: accessToken }
        );

        if (refreshResult?.AccessToken && refreshResult?.RefreshToken) {
            const sessionCookieDomain = readSessionCookieDomain(actionContext);

            setAccessToken(refreshResult.AccessToken, sessionCookieDomain);
            setRefreshToken(refreshResult.RefreshToken, sessionCookieDomain);

            if (sessionCookieDomain) {
                retireHostOnlySessionTokens();
            }

            return {
                ...getAuthToken(),
                IntuitDomeAccountId: nbaId ?? undefined,
                Token: refreshResult.AccessToken,
                RefreshToken: refreshResult.RefreshToken
            };
        }
    } catch (error) {
        console.log('Error during token refresh:', error);

        // The session may still be sound: keep it, and let the guest try again rather than sending them away.
        if (isRefreshUnanswered(error)) {
            actionContext.update(new AccessErrorStateInput(), {
                isAccessError: true,
                reason: 'service-unavailable',
                message: accessErrorMessage('service-unavailable')
            });
            return null;
        }
    }

    actionContext.update(new AccessErrorStateInput(), {
        isAccessError: true,
        reason: SESSION_EXPIRED_REASON,
        message: SESSION_EXPIRED_MESSAGE
    });
    return null;
};
